import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import useAppEvent from 'hooks/useAppEvent';
import { AppEventType } from 'models/Events';
import { Lead, LeadAvailablePipelineAction, LeadStatus } from 'models/Leads';
import { classNames } from 'utils/classNameUtils';
import useLeadCountdownDisplayValue from '../countdown/useLeadCountdownDisplayValue';
import Button from '../../../button/Button';
import PopoverStickyOnHover, {
  PopoverProps,
} from '../../../popover/PopoverStickyOnHover';
import {
  leadActionsPopoverContainerClassName,
  LeadAvailablePipelineActionPriorities,
  leadMessagingActions,
  leadPendingActions,
} from './LeadActionsPopover.constants';
import useLeadPipelineAction from './useLeadPipelineAction';
import LeadActionsPopoverTitle from './LeadActionsPopoverTitle';
import { LeadPopoverActionsList } from './LeadActionsPopover.styles';

interface Props {
  baseId?: string;
  children: ReactNode;
  className?: string;
  containerSelector?: string;
  lead: Lead;
  messagingActionCallback: ({
    action,
    lead,
  }: {
    action: LeadAvailablePipelineAction;
    lead: Lead | undefined;
  }) => void;
  placement?: PopoverProps['placement'];
  updateLeadCallback: () => void;
}

const ACTIONLESS_STATUSES: LeadStatus[] = [
  LeadStatus.PENDING_APPROVED,
] as const;

const LeadActionsPopover = ({
  baseId = 'lead-actions-popover',
  children,
  className = '',
  containerSelector = `.${leadActionsPopoverContainerClassName}`,
  lead,
  messagingActionCallback,
  placement = 'left',
  updateLeadCallback,
}: Props) => {
  const { publish } = useAppEvent();
  const { takeAction } = useLeadPipelineAction();
  const { t } = useTranslation();
  const countdownDisplayValue = useLeadCountdownDisplayValue(lead);

  if (!lead) {
    return children;
  }

  const { availablePipelineActions, status } = lead;
  const isActionless = ACTIONLESS_STATUSES.includes(status);

  if (!availablePipelineActions?.length && !isActionless) {
    return children;
  }

  // Sort actions by priority and alphabetically
  const sortAlphabetically = (
    actionA: LeadAvailablePipelineAction,
    actionB: LeadAvailablePipelineAction,
  ) => {
    return t(`componentLead.actions.${actionA}`).localeCompare(
      t(`componentLead.actions.${actionB}`),
    );
  };

  const sortByPriority = (
    actionA: LeadAvailablePipelineAction,
    actionB: LeadAvailablePipelineAction,
  ) => {
    return (
      LeadAvailablePipelineActionPriorities[actionB] -
      LeadAvailablePipelineActionPriorities[actionA]
    );
  };

  const popoverId = `${baseId}-${lead.uid}`;

  const replaceActionsInFooterWithReviewAction = (
    pipelineActions: LeadAvailablePipelineAction[],
  ) => {
    if (pipelineActions.find((action) => leadPendingActions.includes(action))) {
      return pipelineActions
        .filter((action) => !leadPendingActions.includes(action))
        .concat(LeadAvailablePipelineAction.REVIEW)
        .sort(sortAlphabetically)
        .sort(sortByPriority);
    }

    return pipelineActions
      .slice() // the array might be frozen so a copy needs to be created
      .sort(sortAlphabetically)
      .sort(sortByPriority);
  };

  const handleAction = (action: LeadAvailablePipelineAction) => {
    if (leadMessagingActions.includes(action)) {
      messagingActionCallback({ action, lead });
    } else {
      takeAction({ action, lead, updateLeadCallback });
    }

    publish(AppEventType.POPOVER_TOGGLE, { popoverId });
  };

  const content = isActionless ? (
    <LeadPopoverActionsList>
      <li>
        {t(`componentLead.actionsInfo.popoverContent.noActionInfo`, {
          value: countdownDisplayValue,
          context: status,
        })}
      </li>
    </LeadPopoverActionsList>
  ) : (
    <LeadPopoverActionsList>
      {replaceActionsInFooterWithReviewAction(
        availablePipelineActions ?? [],
      ).map((action) => (
        <li key={action}>
          <Button
            bsStyle="link"
            data-testid={`lead-action-button-${action}`}
            onClick={() => handleAction(action)}
          >
            {t(`componentLead.actions.${action}`)}
          </Button>
        </li>
      ))}
    </LeadPopoverActionsList>
  );

  return (
    <PopoverStickyOnHover
      className={classNames({
        'lead-actions-popover': true,
        [className]: !!className,
      })}
      containerSelector={containerSelector}
      content={content}
      data-testid="lead-actions-popover"
      id={popoverId}
      placement={placement}
      title={<LeadActionsPopoverTitle lead={lead} />}
    >
      <div>{children}</div>
    </PopoverStickyOnHover>
  );
};

LeadActionsPopover.defaultProps = {
  baseId: undefined,
  className: undefined,
  containerSelector: undefined,
  placement: undefined,
};

export default LeadActionsPopover;
